/**
 * useFeatures - Feature flags hook
 * 
 * Provides tenant tier information and feature availability.
 * Uses the /api/features/my-tier endpoint to fetch tier data.
 * 
 * Features:
 * - Caches tier info to avoid repeated API calls
 * - Provides `has(featureName)` helper to check feature access
 * - Auto-refetches when user changes (login/logout)
 */
import { useState, useEffect, useCallback, createContext, useContext, useMemo } from 'react';
import { useAuth } from './useAuth';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Features context
const FeaturesContext = createContext(null);

// Cache key
const FEATURES_CACHE_KEY = 'riskee_features_cache';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Get cached features data
 */
function getCachedFeatures() {
  try {
    const cached = localStorage.getItem(FEATURES_CACHE_KEY);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_TTL_MS) {
        return data;
      }
    }
  } catch {
    // Ignore cache errors
  }
  return null;
}

/**
 * Cache features data
 */
function cacheFeatures(data) {
  try {
    localStorage.setItem(FEATURES_CACHE_KEY, JSON.stringify({
      data,
      timestamp: Date.now(),
    }));
  } catch {
    // Ignore cache errors
  }
}

/**
 * Clear features cache
 */
function clearFeaturesCache() {
  localStorage.removeItem(FEATURES_CACHE_KEY);
}

/**
 * Features Provider component
 */
export function FeaturesProvider({ children }) {
  const { isAuthenticated, getAccessToken, user } = useAuth();
  const [tierData, setTierData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch tier data from API
  const fetchTierData = useCallback(async () => {
    if (!isAuthenticated) {
      setTierData(null);
      clearFeaturesCache();
      return;
    }

    // Check cache first
    const cached = getCachedFeatures();
    if (cached) {
      setTierData(cached);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = getAccessToken();
      const res = await fetch(`${API}/features/my-tier`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch tier information');
      }

      const data = await res.json();
      setTierData(data);
      cacheFeatures(data);
    } catch (err) {
      setError(err.message);
      // On error, use default BASIC tier data
      setTierData({
        tier: 'BASIC',
        tier_name: 'Basic',
        features: ['risk_reporting', 'risk_view', 'action_tracking', 'basic_dashboard'],
        limits: {},
        usage: {},
      });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAccessToken]);

  // Fetch on auth change
  useEffect(() => {
    if (isAuthenticated) {
      fetchTierData();
    } else {
      setTierData(null);
      clearFeaturesCache();
    }
  }, [isAuthenticated, fetchTierData]);

  // Clear cache when user changes
  useEffect(() => {
    clearFeaturesCache();
    if (isAuthenticated) {
      fetchTierData();
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if a feature is available
  const has = useCallback((featureName) => {
    if (!tierData?.features) return false;
    return tierData.features.includes(featureName);
  }, [tierData]);

  // Check if tier meets minimum requirement
  const isTier = useCallback((requiredTier) => {
    if (!tierData?.tier) return false;
    const tierOrder = ['BASIC', 'PRO', 'ENTERPRISE'];
    const currentIndex = tierOrder.indexOf(tierData.tier);
    const requiredIndex = tierOrder.indexOf(requiredTier);
    return currentIndex >= requiredIndex;
  }, [tierData]);

  // Get limit value
  const getLimit = useCallback((limitName) => {
    return tierData?.limits?.[limitName] ?? null;
  }, [tierData]);

  // Get usage value
  const getUsage = useCallback((usageName) => {
    return tierData?.usage?.[usageName] ?? null;
  }, [tierData]);

  // Check if approaching limit
  const isNearLimit = useCallback((limitName, threshold = 0.8) => {
    const limit = tierData?.limits?.[limitName];
    const usage = tierData?.usage?.[limitName.replace('max_', '')];
    if (limit === -1 || limit === null || usage === null) return false;
    return (usage / limit) >= threshold;
  }, [tierData]);

  // Memoized value
  const value = useMemo(() => ({
    // Tier info
    tier: tierData?.tier ?? null,
    tierName: tierData?.tier_name ?? null,
    features: tierData?.features ?? [],
    limits: tierData?.limits ?? {},
    usage: tierData?.usage ?? {},
    
    // State
    loading,
    error,
    
    // Helpers
    has,
    isTier,
    getLimit,
    getUsage,
    isNearLimit,
    refetch: fetchTierData,
  }), [tierData, loading, error, has, isTier, getLimit, getUsage, isNearLimit, fetchTierData]);

  return (
    <FeaturesContext.Provider value={value}>
      {children}
    </FeaturesContext.Provider>
  );
}

/**
 * useFeatures hook
 */
export function useFeatures() {
  const context = useContext(FeaturesContext);
  if (!context) {
    throw new Error('useFeatures must be used within FeaturesProvider');
  }
  return context;
}

/**
 * Feature gate component - conditionally renders children based on feature access
 */
export function FeatureGate({ feature, fallback = null, children }) {
  const { has } = useFeatures();
  
  if (!has(feature)) {
    return fallback;
  }
  
  return children;
}

/**
 * Tier gate component - conditionally renders children based on tier level
 */
export function TierGate({ tier, fallback = null, children }) {
  const { isTier } = useFeatures();
  
  if (!isTier(tier)) {
    return fallback;
  }
  
  return children;
}

export default useFeatures;
